/**
 * PEPTARA — Free gift automation (rule-driven cart reconciler)
 *
 * ONE reconciler, N rules. Each rule owns a hidden `_`-prefixed cart line
 * property — NOT a variant id. That is what lets a customer buy a product AND
 * receive the same product free as two distinct cart lines.
 *
 *   { prop: '_free_gift',   variantId, label, title, image, minSubtotal: 0     }
 *   { prop: '_free_gift_2', variantId, label, title, image, minSubtotal: 25000 }
 *
 * Invariant, enforced on every cart change:
 *
 *   qualifies  = paidQty > 0 AND paidSubtotal >= rule.minSubtotal
 *   qualifies  -> exactly ONE line carrying rule.prop, quantity 1, correct variant
 *   !qualifies -> zero lines carrying rule.prop
 *
 * `paidSubtotal` EXCLUDES every gift line, so a gift can never help pay for the
 * next gift. It is computed in exactly one place (`paidTotals`) and every
 * consumer — the engine and the progress meter alike — reads from it.
 *
 * A theme can add a cart line but it CANNOT price one: Shopify takes the price
 * from the variant. Nothing here attempts to zero a price. The gift must be a
 * $0.00 variant (or be zeroed by a native automatic discount).
 *
 * Configured by snippets/pp-free-gift.liquid. Exposes window.PPFreeGift.
 */

// Captured before anything else in this module runs. Our OWN cart writes go
// through this reference and carry a marker header, so they can never
// re-trigger our own observers.
const nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

const CONFIG = window.PPFreeGift;
const SOURCE = 'pp-free-gift';
const MARKER = 'X-PP-Gift';
const SETTLED_EVENT = 'pp:gift:settled';
const THEME_CART_EVENT = 'cart:update';
const MAX_PASSES = 8;
const DEBOUNCE_MS = 150;
const SWEEP_DELAYS = [1200, 3000];

/** Every gift property this theme has ever written — the orphan sweep's vocabulary. */
const KNOWN_GIFT_PROPS = ['_free_gift', '_free_gift_2'];

/** Any storefront request that can mutate the cart. */
const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\b)/i;

if (CONFIG && nativeFetch) {
  /* ------------------------------------------------------------------ *
   * Rules
   * ------------------------------------------------------------------ */

  /** @returns {Array<Record<string, any>>} rules that can actually be fulfilled right now */
  const activeRules = () =>
    (Array.isArray(CONFIG.rules) ? CONFIG.rules : []).filter(
      (rule) => rule && rule.prop && Number(rule.variantId) > 0 && rule.available !== false
    );

  /**
   * The gift property a line carries, or null. The property — never the variant
   * id — identifies a gift line.
   * @param {Record<string, any>} item
   * @returns {string | null}
   */
  const giftPropOf = (item) => {
    const props = item && item.properties;
    if (!props) return null;
    for (const key of KNOWN_GIFT_PROPS) if (props[key]) return key;
    // Anything the theme ever prefixed as a gift still counts, so a renamed
    // rule leaves a sweepable orphan rather than an invisible squatter.
    for (const key of Object.keys(props)) if (/^_free_gift/.test(key) && props[key]) return key;
    return null;
  };

  /** @param {Record<string, any>} item */
  const isGiftLine = (item) => giftPropOf(item) !== null;

  /**
   * THE single definition of "what the customer is actually paying". Gift lines
   * are excluded from both the subtotal and the quantity.
   * @param {Record<string, any>} cart
   * @returns {{ subtotal: number, qty: number }}
   */
  const paidTotals = (cart) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    let subtotal = 0;
    let qty = 0;
    for (const item of items) {
      if (isGiftLine(item)) continue;
      const line = item.final_line_price != null ? item.final_line_price : item.line_price;
      subtotal += Math.round(Number(line) || 0);
      qty += Number(item.quantity) || 0;
    }
    return { subtotal, qty };
  };

  /* ------------------------------------------------------------------ *
   * Cart I/O — always through the captured native fetch, always marked
   * ------------------------------------------------------------------ */

  const getCart = () =>
    nativeFetch('/cart.js', { headers: { Accept: 'application/json', [MARKER]: '1' } }).then((r) => r.json());

  /**
   * @param {string} url
   * @param {Record<string, any>} body
   */
  const postCart = (url, body) =>
    nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [MARKER]: '1' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  /**
   * The property VALUE is the merchant's internal note for this promotion — it
   * rides along on the line (and into the order) for reporting and for use as a
   * discount condition. It is hidden from the customer by the `_` prefix.
   * @param {Record<string, any>} rule
   */
  const addGift = (rule) =>
    postCart('/cart/add.js', {
      items: [
        {
          id: Number(rule.variantId),
          quantity: 1,
          properties: { [rule.prop]: rule.label || 'true' },
        },
      ],
    });

  /**
   * @param {string} key - Line item key (unique per line).
   * @param {number} quantity
   */
  const changeLine = (key, quantity) => postCart('/cart/change.js', { id: key, quantity });

  /* ------------------------------------------------------------------ *
   * Reconcile — one mutation per pass, re-read, repeat until clean
   * ------------------------------------------------------------------ */

  /**
   * The single next correction the cart needs, or null when it already
   * satisfies every rule.
   * @param {Record<string, any>} cart
   */
  const nextCorrection = (cart) => {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const rules = activeRules();
    const activeProps = new Set(rules.map((rule) => rule.prop));

    // Orphans first: a gift line whose rule no longer exists (promotion
    // switched off, gift product unpublished, property renamed) is dead weight
    // in a live cart.
    for (const item of items) {
      const prop = giftPropOf(item);
      if (prop && !activeProps.has(prop)) {
        return { type: 'remove', key: item.key, prop, reason: 'orphan' };
      }
    }

    const { subtotal, qty } = paidTotals(cart);

    for (const rule of rules) {
      const lines = items.filter((item) => giftPropOf(item) === rule.prop);
      const qualifies = qty > 0 && subtotal >= Number(rule.minSubtotal || 0);

      if (qualifies) {
        if (lines.length === 0) return { type: 'add', rule, prop: rule.prop };
        if (lines.length > 1) {
          return { type: 'remove', key: lines[lines.length - 1].key, prop: rule.prop, reason: 'duplicate' };
        }
        // The merchant repointed the rule at a different product: drop the
        // stale line, and the next pass adds the correct one.
        if (Number(lines[0].variant_id) !== Number(rule.variantId)) {
          return { type: 'remove', key: lines[0].key, prop: rule.prop, reason: 'stale-variant' };
        }
        if (Number(lines[0].quantity) !== 1) {
          return { type: 'quantity', key: lines[0].key, quantity: 1, prop: rule.prop };
        }
      } else if (lines.length > 0) {
        return { type: 'remove', key: lines[0].key, prop: rule.prop, reason: 'not-qualified' };
      }
    }

    return null;
  };

  /**
   * @typedef {Object} ReconcileResult
   * @property {boolean} changed
   * @property {string[]} added - gift properties added during this run
   * @property {Array<{ prop: string, key: string }>} removed
   * @property {boolean} giftAdded - legacy alias kept for pp-cart-toast
   * @property {boolean} giftRemoved
   * @property {Record<string, any>} cart
   * @property {number} paidSubtotal
   * @property {number} paidQty
   */

  /** @returns {ReconcileResult} */
  const emptyResult = () => ({
    changed: false,
    added: [],
    removed: [],
    mutations: 0,
    giftAdded: false,
    giftRemoved: false,
    cart: {},
    paidSubtotal: 0,
    paidQty: 0,
  });

  /** @returns {Promise<ReconcileResult>} */
  const runReconcile = async () => {
    let cart = await getCart();
    /** @type {string[]} */
    const added = [];
    /** @type {Array<{ prop: string, key: string }>} */
    const removed = [];
    let changed = false;
    let mutations = 0;

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      const fix = nextCorrection(cart);
      if (!fix) break;

      let response = null;
      if (fix.type === 'add') {
        response = await addGift(fix.rule);
      } else if (fix.type === 'remove') {
        response = await changeLine(fix.key, 0);
      } else {
        response = await changeLine(fix.key, fix.quantity);
      }

      // A rejected correction (sold out, stale line key) must not spin the loop.
      if (!response || response.status) break;

      if (fix.type === 'add') added.push(fix.prop);
      if (fix.type === 'remove') removed.push({ prop: fix.prop, key: fix.key });
      mutations += 1;
      changed = true;
      cart = await getCart();
    }

    const totals = paidTotals(cart);
    return {
      changed,
      added,
      removed,
      mutations,
      giftAdded: added.length > 0,
      giftRemoved: removed.length > 0,
      cart,
      paidSubtotal: totals.subtotal,
      paidQty: totals.qty,
    };
  };

  /* ------------------------------------------------------------------ *
   * Money — mirrors Liquid's `money` filter, because this theme prints
   * amounts server-side and updates them client-side. Intl.NumberFormat
   * would disagree with the Liquid sitting next to it.
   * ------------------------------------------------------------------ */

  /**
   * @param {number} cents
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  const withDelimiters = (cents, precision, thousands, decimal) => {
    const value = (Math.round(Number(cents) || 0) / 100).toFixed(precision);
    const parts = value.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? whole + decimal + parts[1] : whole;
  };

  /** @param {number} cents */
  const formatMoney = (cents) => {
    const format = CONFIG.moneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let body;
    switch (match[1]) {
      case 'amount_no_decimals':
        body = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        body = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        body = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        body = withDelimiters(cents, 2, '\u0027', '.');
        break;
      case 'amount_with_space_separator':
        body = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        body = withDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_period_and_space_separator':
        body = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        body = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, body);
  };

  /* ------------------------------------------------------------------ *
   * Repaint — our writes happen behind the theme's back, so nothing
   * repaints unless we do it.
   * ------------------------------------------------------------------ */

  /** @param {string} value */
  const cssEscape = (value) =>
    window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(value)
      : String(value).replace(/["\\]/g, '\\$&');

  /**
   * Header cart badge(s), absolute count. A bare number — the same convention
   * the Liquid that printed it uses.
   * @param {Record<string, any>} cart
   */
  const updateBadge = (cart) => {
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('.pp-header__cart').forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      let badge = link.querySelector('.pp-header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'pp-header__cart-count';
          link.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
      link.setAttribute('aria-label', `Cart (${count} items)`);
    });
  };

  /**
   * Every other counter the theme renders. Each element keeps its own text
   * convention: the cart heading holds a bare number, the subtotal holds a
   * money-formatted amount tagged with its value in cents.
   * @param {Record<string, any>} cart
   */
  const updateCounters = (cart) => {
    updateBadge(cart);
    const count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('.pp-cart__count').forEach((el) => {
      el.textContent = String(count);
    });
    const total = cart && typeof cart.total_price === 'number' ? cart.total_price : null;
    if (total != null) {
      document.querySelectorAll('.pp-cart__subtotal').forEach((el) => {
        el.setAttribute('data-pp-cents', String(total));
        el.textContent = formatMoney(total);
      });
    }
  };

  /**
   * Re-broadcast on the theme's own cart event, built the way the theme builds
   * it, tagged with our source so our own listener ignores it instead of
   * looping.
   * @param {ReconcileResult} result
   */
  const dispatchThemeCartUpdate = (result) => {
    if (!result.changed) return;
    const cart = result.cart || {};
    document.dispatchEvent(
      new CustomEvent(THEME_CART_EVENT, {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: { source: SOURCE, itemCount: cart.item_count },
        },
      })
    );
  };

  /**
   * Our settled state, broadcast after EVERY pass — including passes that
   * changed nothing, because a plain quantity edit moves the progress UI
   * without touching a gift line.
   * @param {ReconcileResult} result
   */
  /**
   * The last settled cart, kept so the UI can answer "will this add earn a
   * gift?" with no network at all. Three sequential cart round-trips is a
   * second or more on a real connection — far too slow for a toast.
   * @type {{ paidSubtotal: number, paidQty: number, present: Record<string, boolean> }}
   */
  let lastSettled = { paidSubtotal: 0, paidQty: 0, present: {} };
  /** Until the first pass lands, `lastSettled` is a guess, not a fact. */
  let settledOnce = false;

  // Liquid renders the cart's qualifying totals into the config, so prediction
  // is exact from the very first paint instead of waiting for our own first
  // reconcile — which on a slow connection a fast click can beat.
  if (CONFIG.initial && typeof CONFIG.initial.paidSubtotal === 'number') {
    lastSettled = {
      paidSubtotal: CONFIG.initial.paidSubtotal,
      paidQty: Number(CONFIG.initial.paidQty) || 0,
      present: CONFIG.initial.present || {},
    };
    settledOnce = true;
  }

  /**
   * Predict, synchronously, which rules a pending add would newly satisfy.
   * Optimistic by design: the reconciler is still the authority and the caller
   * is expected to correct itself when the real result lands.
   * @param {number} addedCents - value of the line(s) just added
   * @param {number} addedQty
   * @returns {Array<{ prop: string, title: string }>}
   */
  const predict = (addedCents, addedQty) => {
    // No settled cart yet: stay silent rather than promise a gift the customer
    // may already have.
    if (!settledOnce) return [];
    const subtotal = lastSettled.paidSubtotal + (Number(addedCents) || 0);
    const qty = lastSettled.paidQty + (Number(addedQty) || 0);
    return activeRules()
      .filter(
        (rule) =>
          !lastSettled.present[rule.prop] && qty > 0 && subtotal >= Number(rule.minSubtotal || 0)
      )
      .map((rule) => ({ prop: rule.prop, title: rule.title || '' }));
  };

  /** @param {ReconcileResult} result */
  const broadcast = (result) => {
    const items = Array.isArray(result.cart && result.cart.items) ? result.cart.items : [];
    const rules = activeRules().map((rule) => ({
      prop: rule.prop,
      title: rule.title || '',
      image: rule.image || '',
      minSubtotal: Number(rule.minSubtotal || 0),
      unlocked: result.paidQty > 0 && result.paidSubtotal >= Number(rule.minSubtotal || 0),
      present: items.some((item) => giftPropOf(item) === rule.prop),
      justAdded: result.added.indexOf(rule.prop) !== -1,
    }));
    lastSettled = {
      paidSubtotal: result.paidSubtotal,
      paidQty: result.paidQty,
      present: rules.reduce((acc, rule) => {
        acc[rule.prop] = rule.present;
        return acc;
      }, /** @type {Record<string, boolean>} */ ({})),
    };
    settledOnce = true;
    document.dispatchEvent(
      new CustomEvent(SETTLED_EVENT, {
        detail: {
          cart: result.cart,
          paidSubtotal: result.paidSubtotal,
          paidQty: result.paidQty,
          rules,
          changed: result.changed,
        },
      })
    );
  };

  const onCartPage = () => /(^|\/)cart\/?($|\?)/.test(window.location.pathname);

  /**
   * The two directions are NOT symmetrical: a removed line can be dropped from
   * the DOM directly, but only Liquid can draw a new row or the empty state.
   * Never reload when nothing changed — that kills toasts mid-render and reads
   * as "the page refreshed itself".
   * @param {ReconcileResult} result
   */
  const syncCartPageDom = (result) => {
    if (!result.changed || !onCartPage()) return;
    const rows = document.querySelectorAll('[data-pp-line]');
    if (!rows.length) return; // nothing server-rendered to keep in sync

    const cart = result.cart || {};
    if (cart.item_count === 0 || result.added.length > 0) {
      window.location.reload();
      return;
    }
    result.removed.forEach(({ key }) => {
      const row = document.querySelector(`[data-pp-line][data-pp-key="${cssEscape(key)}"]`);
      if (row) row.remove();
    });
  };

  /* ------------------------------------------------------------------ *
   * Public entry point — re-entrancy latch, never two runs at once
   * ------------------------------------------------------------------ */

  let running = false;
  let queued = false;
  /**
   * Callers that arrived while a cycle was already in flight. Each records the
   * first pass that could legitimately answer it: a pass already reading the
   * cart when the caller arrived predates their change, so answering them with
   * it hands back a stale cart.
   * @type {Array<{ minPass: number, resolve: (result: ReconcileResult) => void }>}
   */
  let waiters = [];
  /** Monotonic count of passes started, used to date those waiters. */
  let passSeq = 0;
  /** The cycle currently accumulating, so late callers can record a baseline. */
  let cycleResult = emptyResult();
  /** ANDed across every caller in the current cycle: if any caller does not
   *  repaint for itself, we repaint. */
  let cycleOwnsRepaint = true;

  /**
   * Repaint after a pass. `cycle` carries everything that has changed so far
   * (so counters and the meter show the settled truth); `pass` carries only
   * this pass's deltas, because the theme event and the cart-page DOM work
   * must fire once per actual change — replaying the accumulated set would
   * reload the cart page again on every follow-up pass.
   * @param {ReconcileResult} pass
   * @param {ReconcileResult} cycle
   */
  const finish = (pass, cycle) => {
    broadcast(cycle);
    if (!cycleOwnsRepaint) {
      updateCounters(cycle.cart);
      dispatchThemeCartUpdate(pass);
      syncCartPageDom(pass);
    }
  };

  /**
   * Release only the waiters whose awaited pass has now run. The rest keep
   * waiting — they queued another pass, so one is guaranteed to come.
   * @param {ReconcileResult} result
   */
  const releaseWaiters = (result) => {
    const ready = waiters.filter((w) => w.minPass <= passSeq);
    waiters = waiters.filter((w) => w.minPass > passSeq);
    ready.forEach((w) => {
      // Report only what happened from this caller's arrival onward. Handing
      // back the whole cycle would credit them with another caller's work.
      const added = result.added.slice(w.baseAdded);
      const removed = result.removed.slice(w.baseRemoved);
      w.resolve({
        changed: result.mutations > w.baseMutations,
        added,
        removed,
        mutations: result.mutations - w.baseMutations,
        giftAdded: added.length > 0,
        giftRemoved: removed.length > 0,
        cart: result.cart,
        paidSubtotal: result.paidSubtotal,
        paidQty: result.paidQty,
      });
    });
  };

  /** @param {ReconcileResult} result */
  const releaseAllWaiters = (result) => {
    const pending = waiters;
    waiters = [];
    pending.forEach((w) => w.resolve(result));
  };

  /**
   * Resolves the caller of the pass currently in flight. Callers are answered
   * as soon as THEIR work is done — not when the whole cycle finally goes
   * quiet. Verification sweeps keep re-queuing passes for up to three seconds;
   * making the add-to-cart toast wait for those meant the "free gift added"
   * note appeared just as the toast was dismissing itself.
   * @type {((result: ReconcileResult) => void) | null}
   */
  let settleCaller = null;

  /** @param {ReconcileResult} result */
  const answer = (result) => {
    if (settleCaller) {
      const resolve = settleCaller;
      settleCaller = null;
      resolve(result);
    }
    releaseWaiters(result);
  };

  /**
   * Drain: run, and if a signal arrived while we were running, run again.
   * Cart writes are never issued in parallel. The result is accumulated across
   * passes — a follow-up pass finds the cart already correct, and returning
   * only that one would tell the caller "nothing happened" for work we just
   * did (silent toast, and no reload to draw a new gift row on the cart page).
   */
  const drain = async () => {
    running = true;
    let result = emptyResult();
    cycleResult = result;
    try {
      do {
        queued = false;
        passSeq += 1;
        const pass = await runReconcile();
        result = {
          changed: result.changed || pass.changed,
          added: result.added.concat(pass.added),
          removed: result.removed.concat(pass.removed),
          mutations: result.mutations + pass.mutations,
          giftAdded: result.giftAdded || pass.giftAdded,
          giftRemoved: result.giftRemoved || pass.giftRemoved,
          cart: pass.cart,
          paidSubtotal: pass.paidSubtotal,
          paidQty: pass.paidQty,
        };
        cycleResult = result;
        finish(pass, result);
        // The cart is correct as of this pass, so answer immediately. Any
        // queued pass is a re-verification, not something the caller waits on.
        answer(result);
      } while (queued);
    } finally {
      running = false;
      queued = false;
    }
    return result;
  };

  /**
   * @param {{ ownsRepaint?: boolean }} [options] - `ownsRepaint` is for callers
   *   that re-render the cart section themselves (the cart page): we skip the
   *   DOM work but still broadcast the settled state.
   * @returns {Promise<ReconcileResult>}
   */
  const reconcile = (options) => {
    const opts = options || {};
    if (!running) cycleOwnsRepaint = true;
    cycleOwnsRepaint = cycleOwnsRepaint && opts.ownsRepaint === true;

    if (running) {
      // A pass is already reading the cart; it cannot speak for a change made
      // after it started. Queue another and wait for that one.
      queued = true;
      const minPass = passSeq + 1;
      const baseAdded = cycleResult.added.length;
      const baseRemoved = cycleResult.removed.length;
      const baseMutations = cycleResult.mutations;
      return new Promise((resolve) =>
        waiters.push({ minPass, baseAdded, baseRemoved, baseMutations, resolve })
      );
    }

    const answered = new Promise((resolve) => {
      settleCaller = resolve;
    });

    // Network errors fail silently: the cart is re-read on the next signal.
    drain().catch((error) => {
      console.warn('[pp-free-gift]', error);
      running = false;
      queued = false;
      const fallback = emptyResult();
      if (settleCaller) {
        const resolve = settleCaller;
        settleCaller = null;
        resolve(fallback);
      }
      releaseAllWaiters(fallback);
    });

    return answered;
  };

  CONFIG.reconcile = reconcile;
  CONFIG.updateBadge = updateBadge;
  CONFIG.updateCounters = updateCounters;
  CONFIG.isGiftLine = isGiftLine;
  CONFIG.giftPropOf = giftPropOf;
  CONFIG.paidTotals = paidTotals;
  CONFIG.formatMoney = formatMoney;
  CONFIG.predict = predict;
  CONFIG.settledEvent = SETTLED_EVENT;

  /* ------------------------------------------------------------------ *
   * Change signals — a cart write must reach us no matter who performed it.
   * ------------------------------------------------------------------ */

  /** @type {ReturnType<typeof setTimeout> | null} */
  let debounceTimer = null;
  /** @param {boolean} [withSweeps] */
  const schedule = (withSweeps) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      reconcile();
      if (withSweeps !== false) {
        // Verification sweeps: reconcile is idempotent and costs one GET when
        // the cart is already correct, so a missed signal self-corrects instead
        // of stranding the customer until their next page load.
        SWEEP_DELAYS.forEach((delay) => {
          setTimeout(() => {
            installFetchHook(); // re-chain if a late app script displaced us
            reconcile();
          }, delay);
        });
      }
    }, DEBOUNCE_MS);
  };

  /**
   * 1. fetch. Third-party app scripts (chat widgets, upsells, analytics,
   *    affiliate apps) patch window.fetch too, and whichever assigns LAST wins.
   *    An app that captured native fetch before us and reassigns after us would
   *    silently bypass this hook — the gift then only appears after a refresh,
   *    intermittently, depending on load order. So we chain onto whatever is
   *    installed AT THIS MOMENT and re-install whenever we find ourselves
   *    displaced.
   */
  function installFetchHook() {
    const current = window.fetch;
    if (typeof current !== 'function') return;
    if (/** @type {any} */ (current).__ppGiftHook) return;

    /** @this {any} */
    const hooked = function (input, init) {
      let url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (error) {
        url = '';
      }
      let ours = false;
      try {
        const headers = init && init.headers;
        if (headers) {
          ours = Boolean(typeof headers.get === 'function' ? headers.get(MARKER) : headers[MARKER]);
        }
      } catch (error) {
        ours = false;
      }
      const promise = current.apply(this, arguments);
      if (!ours && CART_WRITE.test(url)) {
        promise.then(
          (response) => {
            if (response && response.ok) schedule();
          },
          () => {}
        );
      }
      return promise;
    };
    /** @type {any} */ (hooked).__ppGiftHook = true;
    try {
      window.fetch = hooked;
    } catch (error) {
      /* a frozen fetch just means we lean on the other four signals */
    }
  }

  /** 2. XMLHttpRequest — apps and older cart code still use it. */
  function installXhrHook() {
    const XHR = window.XMLHttpRequest;
    if (!XHR || !XHR.prototype || /** @type {any} */ (XHR.prototype).__ppGiftHook) return;
    const open = XHR.prototype.open;
    const send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      /** @type {any} */ (this).__ppUrl = url;
      return open.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      this.addEventListener('load', () => {
        const url = String(/** @type {any} */ (this).__ppUrl || '');
        if (this.status >= 200 && this.status < 300 && CART_WRITE.test(url)) schedule();
      });
      return send.apply(this, arguments);
    };
    /** @type {any} */ (XHR.prototype).__ppGiftHook = true;
  }

  // 3. Form submits, capture phase — so we see them even when a handler calls
  //    preventDefault()/stopPropagation(). The add itself is asynchronous, so
  //    give it a beat; reconcile is idempotent, an early pass finds nothing.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      setTimeout(() => schedule(), 400);
    },
    true
  );

  // 4. The theme's own cart event — ignoring the ones we dispatched ourselves,
  //    or the two observers wrap each other and loop.
  document.addEventListener(THEME_CART_EVENT, (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail && detail.data && detail.data.source === SOURCE) return;
    schedule();
  });

  // 5. Returning to the tab, and back/forward-cache restores (which replay a
  //    page rendered against a cart that may have moved on since).
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule(false);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule(false);
  });

  /** Initial-load enforcement: a stale saved cart heals on page load. */
  const init = () => {
    installFetchHook();
    installXhrHook();
    reconcile();
    SWEEP_DELAYS.forEach((delay) => {
      setTimeout(() => {
        installFetchHook();
        reconcile();
      }, delay);
    });
  };

  // Hook fetch/XHR as early as possible; reconcile once the DOM exists, so the
  // repaint has something to write to.
  installFetchHook();
  installXhrHook();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
